import { type ModelConfig } from '@meridian-ssa/shared';
import { useState } from 'react';

export function EditConfigModal({
  config,
  onClose,
  onSuccess,
}: {
  config: ModelConfig;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [name, setName] = useState(config.name);
  const [temperature, setTemperature] = useState(config.temperature ?? 0.7);
  const [maxTokens, setMaxTokens] = useState(config.max_tokens ?? 16384);
  const [systemPrompt, setSystemPrompt] = useState(config.system_prompt ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const r = await fetch(`/api/eval/configs/${config.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          temperature,
          max_tokens: maxTokens,
          system_prompt: systemPrompt.trim() || null,
        }),
      });

      if (!r.ok) {
        const errorData = await r.json();
        throw new Error(errorData.error || 'Failed to update config');
      }

      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        padding: '2rem',
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#47331F',
            marginBottom: '1.5rem',
          }}
        >
          Edit Configuration
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#47331F',
                marginBottom: '0.5rem',
              }}
            >
              Configuration Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1C9C4',
                borderRadius: '6px',
                fontSize: '0.9375rem',
              }}
            />
          </div>

          {/* Read-only fields */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#47331F',
                marginBottom: '0.5rem',
              }}
            >
              Provider (read-only)
            </label>
            <div
              style={{
                padding: '0.75rem',
                background: 'rgba(239, 235, 229, 0.5)',
                borderRadius: '6px',
                color: '#8B7764',
              }}
            >
              {config.provider}
            </div>
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#47331F',
                marginBottom: '0.5rem',
              }}
            >
              Model (read-only)
            </label>
            <div
              style={{
                padding: '0.75rem',
                background: 'rgba(239, 235, 229, 0.5)',
                borderRadius: '6px',
                color: '#8B7764',
              }}
            >
              {config.api_model}
            </div>
          </div>

          {/* Temperature */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#47331F',
                marginBottom: '0.5rem',
              }}
            >
              Temperature: {temperature}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={temperature}
              onChange={e => setTemperature(parseFloat(e.target.value))}
              style={{ width: '100%', accentColor: '#B7343A' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
                color: '#A8988A',
                marginTop: '0.25rem',
              }}
            >
              <span>0 (Deterministic)</span>
              <span>2 (Creative)</span>
            </div>
          </div>

          {/* Max Tokens */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#47331F',
                marginBottom: '0.5rem',
              }}
            >
              Max Tokens
            </label>
            <input
              type="number"
              value={maxTokens}
              onChange={e => setMaxTokens(parseInt(e.target.value, 10))}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1C9C4',
                borderRadius: '6px',
                fontSize: '0.9375rem',
              }}
            />
          </div>

          {/* System Prompt */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#47331F',
                marginBottom: '0.5rem',
              }}
            >
              Custom System Prompt (optional)
            </label>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #D1C9C4',
                borderRadius: '6px',
                fontSize: '0.9375rem',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
              placeholder="Leave empty to use default system prompt"
            />
          </div>

          {error && (
            <div
              className="error"
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                fontSize: '0.875rem',
              }}
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#47331F',
                border: '1px solid #D1C9C4',
                borderRadius: '6px',
                fontSize: '0.9375rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#B7343A',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9375rem',
                fontWeight: 500,
                cursor: submitting || !name.trim() ? 'not-allowed' : 'pointer',
                opacity: submitting || !name.trim() ? 0.5 : 1,
              }}
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
